// === CONSTANTES GLOBALES ===

import { isMobileOrTablet } from './utils/DeviceUtils';

// Grille de combat — dimensions adaptées mobile/desktop
// Desktop : tiles 64px, grille 512px dans un canvas 800px
// Mobile portrait : tiles 50px, grille 400px dans un canvas 450px
const _mob = isMobileOrTablet();
export const GRID = {
  COLS: 8,
  ROWS: 8,
  TILE_SIZE: _mob ? 50 : 64,
  ORIGIN_X: _mob ? 25 : 48,
  ORIGIN_Y: _mob ? 25 : 38,
};

// Couleurs (hex Phaser)
export const COLORS = {
  TILE_LIGHT: 0x2a3a5c,
  TILE_DARK: 0x1e2d4a,
  TILE_HOVER: 0x3a5a8c,
  TILE_MOVE: 0x2ecc71,
  TILE_ATTACK: 0xe74c3c,

  PLAYER: 0x3498db,
  ENEMY: 0xe74c3c,
  PLAYER_OUTLINE: 0x2980b9,
  ENEMY_OUTLINE: 0xc0392b,

  HP_GREEN: 0x2ecc71,
  HP_RED: 0xe74c3c,
  HP_BG: 0x333333,
  TEXT_WHITE: '#ffffff',
  TEXT_GOLD: '#f1c40f',
} as const;

// Stats de base joueur (capitaine par défaut)
export const PLAYER_STATS = {
  hp: 20,
  maxHp: 20,
  attack: 4,
  defense: 1,
  pa: 4,
  pm: 3,
} as const;

// === CLASSES DE SURVIVANTS ===
import { SurvivorClass } from './systems/CampManager';
import { CombatStats } from './types/Combat.types';

export interface ClassDefinition {
  id: SurvivorClass;
  name: string;
  emoji: string;
  color: number;
  outline: number;
  description: string;
  stats: CombatStats;
  /** Bonus passif unique de la classe */
  passive: string;
  /** Portée de base pour l'attaque (1 = mêlée) */
  baseRange: number;
}

export const CLASS_DEFINITIONS: Record<SurvivorClass, ClassDefinition> = {
  warrior: {
    id: 'warrior',
    name: 'Guerrier',
    emoji: '⚔️',
    color: 0xe74c3c,
    outline: 0xc0392b,
    description: 'Tanky et puissant au corps à corps.',
    stats: { hp: 24, maxHp: 24, attack: 5, defense: 2, pa: 4, pm: 2 },
    passive: '+1 DEF par ennemi adjacent',
    baseRange: 1,
  },
  ranger: {
    id: 'ranger',
    name: 'Tireur',
    emoji: '🏹',
    color: 0x2ecc71,
    outline: 0x27ae60,
    description: 'Rapide et mortel à distance.',
    stats: { hp: 16, maxHp: 16, attack: 4, defense: 0, pa: 4, pm: 3 },
    passive: '+1 portée sur toutes les attaques',
    baseRange: 2,
  },
  medic: {
    id: 'medic',
    name: 'Médecin',
    emoji: '💊',
    color: 0x3498db,
    outline: 0x2980b9,
    description: 'Soigne chaque tour et résistant.',
    stats: { hp: 20, maxHp: 20, attack: 3, defense: 1, pa: 5, pm: 2 },
    passive: 'Régénère 1 HP par tour',
    baseRange: 1,
  },
};

// Compétences joueur
import { EnemyDefinition, SkillDefinition } from './types/Combat.types';

/** Skills de base (disponibles pour tous) */
export const BASE_SKILLS: SkillDefinition[] = [
  {
    id: 'attack',
    name: 'Attaque',
    emoji: '⚔️',
    paCost: 2,
    range: 1,
    damage: 1,
    knockback: 0,
    description: 'Attaque de mêlée basique',
  },
  {
    id: 'cannon',
    name: 'Coup de Canon',
    emoji: '💣',
    paCost: 4,
    range: 3,
    minRange: 2,
    damage: 1,
    knockback: 0,
    description: 'Tir lourd à distance. 1 tir par tour.',
  },
  {
    id: 'savage',
    name: 'Frappe Sauvage',
    emoji: '🔨',
    paCost: 3,
    range: 1,
    damage: 1.5,
    knockback: 1,
    description: 'Gros dégâts + recul 1 case',
  },
];

/** Skills spéciaux par classe (débloqués en run) */
export const CLASS_SKILLS: Record<string, SkillDefinition[]> = {
  warrior: [
    {
      id: 'taunt',
      name: 'Provocation',
      emoji: '🗣️',
      paCost: 2,
      range: 1,
      damage: 0,
      knockback: 0,
      description: 'Force un ennemi adjacent à cibler le guerrier pendant 2 tours',
      special: 'taunt',
      classReq: 'warrior',
    },
    {
      id: 'shield_wall',
      name: 'Mur de Bouclier',
      emoji: '🛡️',
      paCost: 3,
      range: 0,
      damage: 0,
      knockback: 0,
      description: '+3 DEF ce tour. Bloque les AoO pour les alliés derrière.',
      special: 'shield',
      classReq: 'warrior',
    },
  ],
  ranger: [
    {
      id: 'pushback_shot',
      name: 'Tir de Recul',
      emoji: '🏹',
      paCost: 2,
      range: 3,
      damage: 0.6,
      knockback: 2,
      description: 'Dégâts réduits + repousse de 2 cases (anti-kite)',
      classReq: 'ranger',
    },
    {
      id: 'arrow_rain',
      name: 'Pluie de Flèches',
      emoji: '🌧️',
      paCost: 4,
      range: 4,
      damage: 0.5,
      knockback: 0,
      description: 'Dégâts ×0.5 sur une zone 2×2',
      special: 'aoe',
      aoeRadius: 1,
      classReq: 'ranger',
    },
  ],
  medic: [
    {
      id: 'heal_ally',
      name: 'Soin Ciblé',
      emoji: '💚',
      paCost: 3,
      range: 3,
      damage: 0,
      knockback: 0,
      description: 'Soigne un allié de 5 HP',
      special: 'heal_ally',
      targetAlly: true,
      healAmount: 5,
      classReq: 'medic',
    },
    {
      id: 'purify',
      name: 'Purification',
      emoji: '✨',
      paCost: 2,
      range: 3,
      damage: 0,
      knockback: 0,
      description: 'Retire un debuff (immobilisation, malédiction)',
      special: 'purify',
      targetAlly: true,
      classReq: 'medic',
    },
  ],
};

/** Rétro-compat : skills par défaut (3 de base) */
export const SKILLS = BASE_SKILLS;

// Définitions des types d'ennemis

export const ENEMY_DEFINITIONS: Record<string, EnemyDefinition> = {
  // Zombie classique : équilibré, IA basique
  zombie: {
    type: 'zombie',
    name: 'Zombie Pirate',
    stats: { hp: 8, maxHp: 8, attack: 3, defense: 0, pa: 3, pm: 2 },
    color: 0xe74c3c,
    outline: 0xc0392b,
    emoji: '💀',
  },
  // Brute : lent, tanky, frappe fort
  brute: {
    type: 'brute',
    name: 'Brute Morte',
    stats: { hp: 16, maxHp: 16, attack: 5, defense: 2, pa: 2, pm: 1 },
    color: 0x8e44ad,
    outline: 0x6c3483,
    emoji: '🧟',
  },
  // Fast : rapide, fragile, esquive
  fast: {
    type: 'fast',
    name: 'Rôdeur Spectral',
    stats: { hp: 5, maxHp: 5, attack: 2, defense: 0, pa: 4, pm: 4 },
    color: 0x1abc9c,
    outline: 0x16a085,
    emoji: '👻',
  },
  // Crabe Géant : tanky, lent, haute DEF
  crab: {
    type: 'crab',
    name: 'Crabe Géant',
    stats: { hp: 12, maxHp: 12, attack: 4, defense: 3, pa: 2, pm: 1 },
    color: 0xe67e22,
    outline: 0xd35400,
    emoji: '🦀',
  },
  // Canonnier : attaque à distance, fragile
  gunner: {
    type: 'gunner',
    name: 'Canonnier Mort',
    stats: { hp: 6, maxHp: 6, attack: 5, defense: 0, pa: 3, pm: 1 },
    color: 0x7f8c8d,
    outline: 0x6c7a89,
    emoji: '🔫',
    range: 3,
  },
  // Sirène : esquive, attaque à distance moyenne
  siren: {
    type: 'siren',
    name: 'Sirène Spectrale',
    stats: { hp: 7, maxHp: 7, attack: 2, defense: 0, pa: 4, pm: 3 },
    color: 0x9b59b6,
    outline: 0x8e44ad,
    emoji: '🧜',
    range: 2,
  },
  // Bomber : explose en mourant, dégâts de zone
  bomber: {
    type: 'bomber',
    name: 'Baril Maudit',
    stats: { hp: 4, maxHp: 4, attack: 8, defense: 0, pa: 2, pm: 2 },
    color: 0xe74c3c,
    outline: 0xc0392b,
    emoji: '💣',
  },
  // Maudit : se soigne chaque tour
  cursed: {
    type: 'cursed',
    name: 'Noyé Maudit',
    stats: { hp: 10, maxHp: 10, attack: 3, defense: 1, pa: 3, pm: 2 },
    color: 0x2c3e50,
    outline: 0x1a252f,
    emoji: '🌊',
  },
  // Requin : très rapide, frappe fort, fragile
  shark: {
    type: 'shark',
    name: 'Requin Fantôme',
    stats: { hp: 6, maxHp: 6, attack: 5, defense: 0, pa: 3, pm: 5 },
    color: 0x34495e,
    outline: 0x2c3e50,
    emoji: '🦈',
  },

  // === BOSS ===

  // Boss 1 : Capitaine mort-vivant, tanky + dégâts + mobile
  boss: {
    type: 'boss',
    name: 'Capitaine Maudit',
    stats: { hp: 30, maxHp: 30, attack: 6, defense: 2, pa: 4, pm: 2 },
    color: 0xf39c12,
    outline: 0xe67e22,
    emoji: '👹',
  },
  // Boss 2 : Kraken — énorme, lent, dégâts massifs
  boss_kraken: {
    type: 'boss_kraken',
    name: 'Kraken Abyssal',
    stats: { hp: 40, maxHp: 40, attack: 8, defense: 3, pa: 3, pm: 1 },
    color: 0x2980b9,
    outline: 0x1f6dad,
    emoji: '🐙',
  },
  // Boss 3 : Prêtresse Vaudou — attaque à distance courte, mobile modérée
  // Nerfée : range/PM/PA/ATK/HP réduits (feedback playtest : "intombable, tape de trop loin").
  boss_voodoo: {
    type: 'boss_voodoo',
    name: 'Prêtresse Vaudou',
    stats: { hp: 22, maxHp: 22, attack: 4, defense: 1, pa: 4, pm: 2 },
    color: 0x8e44ad,
    outline: 0x6c3483,
    emoji: '🧙‍♀️',
    range: 2,
  },
  // Boss secret : Captain Jack Warraps (combat 1v1 via Konami code / clic crédits)
  // Stats fortes mais battable avec un bon équipement. Range 3 = sabre + pistolet.
  boss_jack: {
    type: 'boss_jack',
    name: 'Captain Jack Warraps',
    stats: { hp: 50, maxHp: 50, attack: 6, defense: 3, pa: 5, pm: 3 },
    color: 0x7b241c,
    outline: 0x5b1612,
    emoji: '☠️',
    range: 3,
  },
} as const;
